import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'

import apis from 'browser/app/models/apis'
import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'

import { FramesManager } from 'shared-libs/components/view/frames-manager'
import { evaluateExpressionWithScopes } from 'shared-libs/helpers/evaluation'
import { isProductionApi } from 'shared-libs/helpers/utils'
import {
  EventFieldResponse,
  UI2EventFieldProps,
  UI2_EVENT_FIELD_DEFAULT_PROPS,
} from 'shared-libs/components/ui2/fields/ui2-event-field/ui2-event-field-props'
import {
  eventReturnPaths,
  resolveEventMappings,
} from 'shared-libs/components/ui2/fields/ui2-event-field/event-field-mappings'
import {
  isFireInteractiveMode,
  isFireResponseIncomplete,
  resolveExpressionsDeep,
  resolveFireButtonVariant,
  resolveFireLoadingMessage,
  FIRE_DEFAULT_FIRE_BUTTON_VARIANT,
  FIRE_DEFAULT_RETRY_BUTTON_VARIANT,
} from 'shared-libs/components/ui2/fields/core-field/fire-field-props'
import {
  safeStringify,
  FIRE_DEFAULT_DEBOUNCE_MS,
  FIRE_DEFAULT_GENERIC_ERROR_MESSAGE,
  FIRE_DEFAULT_MAX_ATTEMPTS_MESSAGE,
  FIRE_DEFAULT_NO_INTERNET_MESSAGE,
  FIRE_DEFAULT_NO_INTERNET_TITLE,
  FIRE_DEFAULT_TIMEOUT_TITLE,
  FIRE_SLOW_LOADING_THRESHOLD_MS,
} from 'shared-libs/components/ui2/fields/field-ui/fire-field-ui/fire-field-ui-props'

import { UI2Banner } from '../../display/ui2-banner/ui2-banner'
import { UI2FieldShell, IUI2FieldShellProps } from '../field-ui/field-shell/field-shell'
import {
  UI2FireDebugPanel,
  UI2FireInlineLookup,
  UI2FireLazyContent,
  UI2FireLoadingIndicator,
  UI2FireRetryButton,
  UI2FireTriggerButton,
} from '../field-ui/fire-field-ui/fire-field-ui'

type FailureClass = 'retry' | 'exhausted' | 'notEligible'

interface IEventExpression {
  type: 'expression'
  value: string
}

interface IUI2EventFieldWebExtras extends Omit<IBaseProps, 'children'> {
  errors?: any[]
  frames?: FramesManager
  translationTable?: any
  isHorizontalLayout?: boolean
}

/**
 * @uiComponent
 */
export interface IUI2EventFieldProps extends UI2EventFieldProps, IUI2EventFieldWebExtras {}

interface IUI2EventFieldState {
  isLoading: boolean
  isSlowLoading: boolean
  attemptsUsed: number
  lastFailure: EventFieldResponse | null
  lastFailureIsNetwork: boolean
  lastFailureIsTimeout: boolean
  lastFailureClass: FailureClass | null
  hasSucceeded: boolean
  hasFired: boolean
  prevConditionTruthy: boolean
  prevClearTruthy: boolean
  /** The query last submitted in `fireMode: 'inlineLookup'`, in scope as `lookupQuery`. */
  lookupQuery: string
}

// Server events run subscribers inline, and some reach out to a customer system. Allow more headroom
// than the apiField's single outbound call, but still bound it so a hung subscriber can't strand the
// step with no way forward.
const HARD_TIMEOUT_MS = 30000
const DEFAULT_TIMEOUT_BODY =
  'The event took too long to process and was canceled. Please try again.'
const HARD_TIMEOUT_SENTINEL = Symbol('ui2EventField:hardTimeout')
const DEFAULT_FAILURE_TITLE = 'Something went wrong'
const NO_EXECUTION_MESSAGE =
  'This field must be bound to a saved storyboard execution before it can fire an event.'

export class UI2EventField extends React.Component<IUI2EventFieldProps, IUI2EventFieldState> {
  public static defaultProps: Partial<IUI2EventFieldProps> = {
    ...UI2_EVENT_FIELD_DEFAULT_PROPS,
  }

  /**
   * `isRequired` on a fire field means "the call succeeded". The renderer reads this static to
   * synthesize the `is required` error at submit; the shell gets the same predicate as
   * `isValueEmpty` below, so the two can't disagree.
   */
  public static isValueEmptyForRequired(value: EventFieldResponse | undefined): boolean {
    return isFireResponseIncomplete(value)
  }

  private debouncedFire: (() => void) & { cancel: () => void } | null = null
  private lastResolvedInputsKey: string | null = null
  private slowLoadingTimer: ReturnType<typeof setTimeout> | null = null
  private hardTimeoutTimer: ReturnType<typeof setTimeout> | null = null

  constructor(props: IUI2EventFieldProps) {
    super(props)
    this.state = {
      isLoading: false,
      isSlowLoading: false,
      attemptsUsed: 0,
      lastFailure: null,
      lastFailureIsNetwork: false,
      lastFailureIsTimeout: false,
      lastFailureClass: null,
      hasSucceeded: false,
      hasFired: false,
      prevConditionTruthy: false,
      prevClearTruthy: false,
      lookupQuery: '',
    }
    if (props.fireMode === 'onInputsChanged') {
      this.debouncedFire = _.debounce(
        () => this.fire(),
        props.debounceMs ?? FIRE_DEFAULT_DEBOUNCE_MS,
      )
    }
  }

  public componentDidMount() {
    const { fireMode, isReadOnly, isDisabled, value, fireOnMountEvenIfPrefilled } = this.props
    if (isReadOnly || isDisabled) {
      return
    }
    if (fireMode === 'onMount') {
      const hasPrefilled = value !== undefined && value !== null
      if (!hasPrefilled || fireOnMountEvenIfPrefilled) {
        if (this.allInputsPresent()) {
          this.lastResolvedInputsKey = this.resolvedInputsKey()
          this.fire()
        }
      }
    } else if (fireMode === 'onInputsChanged') {
      this.lastResolvedInputsKey = this.resolvedInputsKey()
      this.fire()
    } else if (fireMode === 'onCondition') {
      this.evaluateAndMaybeFireCondition()
    }
    this.evaluateAndMaybeClearResponse()
  }

  public componentDidUpdate(_prev: IUI2EventFieldProps) {
    const mode = this.props.fireMode
    if (mode === 'onInputsChanged') {
      const key = this.resolvedInputsKey()
      if (key !== this.lastResolvedInputsKey) {
        this.lastResolvedInputsKey = key
        this.debouncedFire?.()
      }
    } else if (mode === 'onCondition') {
      this.evaluateAndMaybeFireCondition()
    }
    this.evaluateAndMaybeClearResponse()
  }

  public componentWillUnmount() {
    this.debouncedFire?.cancel()
    this.clearInFlightTimers()
  }

  private evaluateAndMaybeFireCondition() {
    const { fireCondition, frames } = this.props
    if (!fireCondition || !frames) return
    let truthy = false
    try {
      truthy = !!evaluateExpressionWithScopes(frames, fireCondition, {})
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[UI2EventField] fireCondition eval failed', fireCondition, e)
      return
    }
    if (truthy && !this.state.prevConditionTruthy) {
      this.setState({ prevConditionTruthy: true })
      this.fire()
    } else if (!truthy && this.state.prevConditionTruthy) {
      this.setState({ prevConditionTruthy: false })
    }
  }

  private evaluateAndMaybeClearResponse() {
    const { clearResponseCondition, frames, onChange } = this.props
    if (!clearResponseCondition || !frames) return
    let truthy = false
    try {
      truthy = !!evaluateExpressionWithScopes(frames, clearResponseCondition, {})
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[UI2EventField] clearResponseCondition eval failed', clearResponseCondition, e)
      return
    }
    if (truthy && !this.state.prevClearTruthy) {
      this.setState({ prevClearTruthy: true })
      onChange?.(undefined)
    } else if (!truthy && this.state.prevClearTruthy) {
      this.setState({ prevClearTruthy: false })
    }
  }

  private evaluate(expr: IEventExpression, defaultContext: any = {}): unknown {
    const { frames } = this.props
    try {
      return evaluateExpressionWithScopes(frames, expr.value, defaultContext)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[UI2EventField] expression eval failed', expr.value, e)
      return undefined
    }
  }

  private resolveValue<T>(v: T | IEventExpression | undefined, defaultContext: any = {}): T | undefined {
    if (v === undefined) return undefined
    if (_.isObject(v) && (v as IEventExpression).type === 'expression'
        && typeof (v as IEventExpression).value === 'string') {
      return this.evaluate(v as IEventExpression, defaultContext) as T | undefined
    }
    return v as T
  }

  /**
   * Resolves the expression-bearing parts of the request. Output mapping values are resolved here
   * rather than server-side so the server receives literals — the same contract the workflow event
   * handler produces before it writes mappings onto the execution.
   */
  /**
   * Extra scope every request expression is resolved against. `lookupQuery` is what the user
   * submitted in `fireMode: 'inlineLookup'`, and is empty in every other mode.
   */
  private expressionScope(): Record<string, unknown> {
    return { lookupQuery: this.state.lookupQuery }
  }

  private resolveInputs() {
    const scope = this.expressionScope()
    // `lazyDetails` / `lazyOutputMappings` reach the field unmaterialized (the renderer skips
    // `lazy*` keys), so their expressions are resolved here — with `lookupQuery` in scope, at any
    // depth — and layered over their eagerly-materialized counterparts.
    const lazyDetails = resolveExpressionsDeep(
      this.props.lazyDetails, (e) => this.evaluate(e, scope),
    )
    const lazyOutputMappings = resolveExpressionsDeep(
      this.props.lazyOutputMappings, (e) => this.evaluate(e, scope),
    )
    const details = _.merge({}, this.props.details ?? {}, lazyDetails.value ?? {})
    const outputMappings = [
      ...(this.props.outputMappings ?? []),
      ...(lazyOutputMappings.value ?? []),
    ]
    const out = {
      details: {} as Record<string, unknown>,
      outputMappings: [] as Array<{ name?: string, destination: string, value?: unknown }>,
      allPresent: lazyDetails.allPresent && lazyOutputMappings.allPresent,
    }
    for (const k of Object.keys(details)) {
      const resolved = this.resolveValue(details[k], scope)
      if (_.isNil(resolved)) {
        out.allPresent = false
        continue
      }
      out.details[k] = resolved
    }
    for (const mapping of outputMappings) {
      if (!mapping?.destination) continue
      const resolved = mapping.value === undefined
        ? undefined
        : this.resolveValue(mapping.value, scope)
      if (mapping.value !== undefined && _.isNil(resolved)) {
        out.allPresent = false
        continue
      }
      out.outputMappings.push({
        name: mapping.name,
        destination: mapping.destination,
        value: resolved,
      })
    }
    return out
  }

  private allInputsPresent(): boolean {
    return this.resolveInputs().allPresent
  }

  private resolvedInputsKey(): string {
    const { details, outputMappings } = this.resolveInputs()
    return JSON.stringify({ details, outputMappings })
  }

  private getExecutionId(): string | undefined {
    const propEntity = (this.props as any).entity
    const framesEntity = this.props.frames?.getContext('entity')
    const entity = propEntity ?? framesEntity
    if (entity?.isNew) return undefined
    const id = entity?.uniqueId ?? entity?.data?.uniqueId
    return typeof id === 'string' ? id : undefined
  }

  private classifyFailure(isNetworkError: boolean, isTimeoutFailure: boolean,
                          attemptsUsed: number): FailureClass {
    const { retryType = 'never', maxAttempts } = this.props
    let matchesRetryType = false
    // A hard timeout always qualifies for a manual retry regardless of retryType — the request never
    // reached a verdict, so honoring "never" here would strand the user with no way forward.
    if (isTimeoutFailure) {
      matchesRetryType = true
    } else if (retryType === 'never') {
      return 'notEligible'
    } else {
      matchesRetryType = true
    }
    if (!matchesRetryType) return 'notEligible'
    if (maxAttempts !== undefined && attemptsUsed >= maxAttempts) return 'exhausted'
    return 'retry'
  }

  private normalizeError(e: any): { response: EventFieldResponse, isNetworkError: boolean } {
    // Only a request that never received an HTTP response counts as a network error (offline,
    // DNS/TLS/CORS/abort). A live 4xx or 5xx is a server-side failure.
    const httpStatus = typeof e?.status === 'number' ? e.status : 0
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false
    const isNetworkError = offline || !httpStatus || e?.readyState === 0
    const serverError = e?.responseJSON?.errors?.[0]?.message ?? e?.responseJSON?.errorMsg
    return {
      response: {
        success: false,
        resolvedValues: {},
        errorMsg: serverError || e?.message || String(e) || 'Request failed',
        dryRun: this.props.dryRun !== false,
      },
      isNetworkError,
    }
  }

  private clearSlowLoadingTimer() {
    if (this.slowLoadingTimer !== null) {
      clearTimeout(this.slowLoadingTimer)
      this.slowLoadingTimer = null
    }
  }

  private clearHardTimeoutTimer() {
    if (this.hardTimeoutTimer !== null) {
      clearTimeout(this.hardTimeoutTimer)
      this.hardTimeoutTimer = null
    }
  }

  private clearInFlightTimers() {
    this.clearSlowLoadingTimer()
    this.clearHardTimeoutTimer()
  }

  private fire = async () => {
    const { eventType, valueMappings, dryRun, onChange } = this.props
    const executionId = this.getExecutionId()
    const resolved = this.resolveInputs()

    // The endpoint is addressed by execution id, so an unsaved entity has nothing to fire against.
    // Surface that as a plain failure rather than issuing a request that can only 404.
    if (!executionId) {
      const response: EventFieldResponse = {
        success: false,
        resolvedValues: {},
        errorMsg: NO_EXECUTION_MESSAGE,
        dryRun: dryRun !== false,
      }
      this.setState({
        isLoading: false,
        lastFailure: response,
        lastFailureIsNetwork: false,
        lastFailureIsTimeout: false,
        lastFailureClass: 'notEligible',
        hasSucceeded: false,
        hasFired: true,
      })
      onChange?.(response)
      return
    }

    this.setState({ isLoading: true, isSlowLoading: false, hasFired: true })
    this.clearSlowLoadingTimer()
    this.slowLoadingTimer = setTimeout(() => {
      this.slowLoadingTimer = null
      if (this.state.isLoading && !this.state.isSlowLoading) {
        this.setState({ isSlowLoading: true })
      }
    }, FIRE_SLOW_LOADING_THRESHOLD_MS)

    const url = `${apis.getHost()}/1.0/entities/actions/storyboard/fireEvent/`
      + `${encodeURIComponent(executionId)}`
    const body = {
      eventType,
      details: resolved.details,
      outputMappings: resolved.outputMappings,
      returnPaths: eventReturnPaths(valueMappings),
      dryRun: dryRun !== false,
    }
    const req = apis.postJSON<EventFieldResponse>(url, body)
    this.clearHardTimeoutTimer()
    const timeoutRace = new Promise<never>((_resolve, reject) => {
      this.hardTimeoutTimer = setTimeout(() => {
        this.hardTimeoutTimer = null
        reject(HARD_TIMEOUT_SENTINEL)
      }, HARD_TIMEOUT_MS)
    })
    let response: EventFieldResponse
    let isNetworkError = false
    let isTimeoutFailure = false
    try {
      response = await Promise.race([req, timeoutRace])
      // The upload worker can resolve with a structureless value instead of rejecting when a network
      // error fires onloadend with status=0 before onerror. Treat any non-response resolve as one.
      if (!response || typeof (response as any).success !== 'boolean') {
        isNetworkError = true
        response = {
          success: false,
          resolvedValues: {},
          errorMsg: 'Network error',
          dryRun: dryRun !== false,
        }
      }
    } catch (e) {
      if (e === HARD_TIMEOUT_SENTINEL) {
        ;(req as any).cancel?.()
        isTimeoutFailure = true
        isNetworkError = true
        response = {
          success: false,
          resolvedValues: {},
          errorMsg: `Request timed out after ${HARD_TIMEOUT_MS / 1000} seconds.`,
          dryRun: dryRun !== false,
        }
      } else {
        const normalized = this.normalizeError(e)
        response = normalized.response
        isNetworkError = normalized.isNetworkError
      }
    }
    this.clearInFlightTimers()

    const isNoInternet = isNetworkError && !isTimeoutFailure
    const nextAttemptsUsed = this.state.attemptsUsed + (response.success || isNoInternet ? 0 : 1)

    if (response.success) {
      this.setState({
        isLoading: false,
        attemptsUsed: nextAttemptsUsed,
        lastFailure: null,
        lastFailureIsNetwork: false,
        lastFailureIsTimeout: false,
        lastFailureClass: null,
        hasSucceeded: true,
      })
      onChange?.(response)
      this.applyValueMappings(response)
    } else {
      const classification = this.classifyFailure(
        isNetworkError, isTimeoutFailure, nextAttemptsUsed,
      )
      this.setState({
        isLoading: false,
        attemptsUsed: nextAttemptsUsed,
        lastFailure: response,
        lastFailureIsNetwork: isNetworkError,
        lastFailureIsTimeout: isTimeoutFailure,
        lastFailureClass: classification,
        hasSucceeded: false,
      })
      if (classification !== 'retry') {
        onChange?.(response)
      }
    }
  }

  /**
   * Writes each mapping to its own form path. Only on success — a failed event has no results, and
   * writing nulls would clear whatever the destinations already held.
   */
  private applyValueMappings(response: EventFieldResponse) {
    const { valueMappings, frames, onChange } = this.props
    const ownPath = (frames?.getContext('valuePath') ?? []).join('.')
    for (const { destination, value } of resolveEventMappings(valueMappings, frames, response)) {
      // Writing a mapping to the field's own bound path overwrites the response this field just
      // stored there — the two writes land in the same tick and the mapping, going second, wins.
      // Cheap to do by accident, and invisible afterwards, so say so.
      if (ownPath && destination === ownPath) {
        // eslint-disable-next-line no-console
        console.warn(
          `[UI2EventField] valueMappings destination "${destination}" is this field's own bound `
            + 'value path, so it overwrites the response. Map to a different path.'
        )
      }
      onChange?.(value as any, false, destination)
    }
  }

  private handleClick = () => { this.fire() }
  private handleRetry = () => { this.fire() }

  /**
   * An inline-lookup submit is a fire. The query is written to `lookupQueryPath` when the schema
   * asked for one, so the rest of the form can read it, and is always put in scope as `lookupQuery`
   * for the field's own detail expressions.
   */
  private handleLookupSubmit = (query: string) => {
    const { lookupQueryPath, onChange } = this.props
    if (lookupQueryPath) {
      onChange?.(query as any, false, lookupQueryPath)
    }
    this.setState({ lookupQuery: query }, () => this.fire())
  }

  /** The response shape expression props and lazy uiSchemas are resolved against. */
  private responseScope(
    response: EventFieldResponse,
    isNetworkError = false,
    isTimeoutFailure = false,
  ) {
    return {
      eventResponse: {
        success: response.success,
        errorMsg: response.errorMsg,
        dryRun: response.dryRun,
        resolvedValues: response.resolvedValues,
        isNetworkError,
        isTimeout: isTimeoutFailure,
      },
    }
  }

  private evaluateMessageExpression(expression: string | undefined, scope: any): string | null {
    if (!expression) return null
    const resolved = this.evaluate({ type: 'expression', value: expression }, scope)
    return typeof resolved === 'string' && resolved.length > 0 ? resolved : null
  }

  private resolveFailureMessage(response: EventFieldResponse, isNetworkError: boolean,
                                isTimeoutFailure: boolean): string {
    const scope = this.responseScope(response, isNetworkError, isTimeoutFailure)
    const resolved = this.evaluateMessageExpression(this.props.failureMessageExpression, scope)
    if (resolved) return resolved
    if (isTimeoutFailure) return DEFAULT_TIMEOUT_BODY
    if (isNetworkError) return FIRE_DEFAULT_NO_INTERNET_MESSAGE
    return FIRE_DEFAULT_GENERIC_ERROR_MESSAGE
  }

  private buildFailureBody(response: EventFieldResponse, isNetworkError: boolean,
                           isTimeoutFailure: boolean, cls: FailureClass): string {
    const parts: string[] = [
      this.resolveFailureMessage(response, isNetworkError, isTimeoutFailure),
    ]
    if (response.errorMsg) {
      parts.push(`\`${response.errorMsg}\``)
    }
    if (cls === 'retry') {
      const { maxAttempts } = this.props
      const isNoInternet = isNetworkError && !isTimeoutFailure
      if (maxAttempts !== undefined && !isNoInternet) {
        const remaining = Math.max(0, maxAttempts - this.state.attemptsUsed)
        parts.push(`${remaining} ${remaining === 1 ? 'attempt' : 'attempts'} remaining`)
      }
    } else if (cls === 'exhausted') {
      parts.push(this.props.maxAttemptsReachedMessage ?? FIRE_DEFAULT_MAX_ATTEMPTS_MESSAGE)
    }
    return parts.join('\n\n')
  }

  /** Inline text beside a spinner, never a banner. Silent in the auto-fire modes unless opted in. */
  private renderLoading(): React.ReactNode {
    if (!this.state.isLoading) return null
    const message = resolveFireLoadingMessage(this.props)
    if (message === null && !this.state.isSlowLoading) return null
    return (
      <UI2FireLoadingIndicator
        isSlow={this.state.isSlowLoading}
        message={message ?? undefined}
      />
    )
  }

  /**
   * Success renders only what the schema asked for: a lazy uiSchema if given, otherwise a banner
   * carrying `successMessageExpression`. With neither declared, a success is silent in every mode.
   */
  private renderSuccess(response?: EventFieldResponse): React.ReactNode {
    const { lazySuccessUiSchema, successMessageExpression, frames } = this.props
    const result = response ?? this.props.value
    if (!result) return null
    const scope = this.responseScope(result)
    if (lazySuccessUiSchema) {
      return <UI2FireLazyContent uiSchema={lazySuccessUiSchema} scope={scope} frames={frames} />
    }
    const message = this.evaluateMessageExpression(successMessageExpression, scope)
    if (!message) return null
    return (
      <div className='ui2-fireField__successBanner'>
        <UI2Banner mode='success' title={message} />
      </div>
    )
  }

  private renderRetryButton(): React.ReactNode {
    return (
      <UI2FireRetryButton
        variant={resolveFireButtonVariant(
          this.props.retryButtonVariant, FIRE_DEFAULT_RETRY_BUTTON_VARIANT,
        )}
        isLoading={this.state.isLoading}
        onPress={this.handleRetry}
      />
    )
  }

  /**
   * Failure follows the same rule as success, with one exception: a retryable or timed-out failure
   * always surfaces even with nothing declared, because otherwise the retry affordance — and the
   * fact that anything went wrong at all — would be unreachable.
   *
   * With a `lazyFailureUiSchema` the retry button is rendered beneath the custom UI, except in
   * `inlineLookup` mode where the lookup's own submit button is the retry.
   */
  private renderFailure(): React.ReactNode {
    const {
      lastFailure, lastFailureIsNetwork, lastFailureIsTimeout, lastFailureClass,
    } = this.state
    if (!lastFailure || !lastFailureClass) return null
    const { lazyFailureUiSchema, failureMessageExpression, fireMode, frames } = this.props
    const retryActive = lastFailureClass === 'retry'
    const mustSurface = retryActive || lastFailureIsTimeout

    if (lazyFailureUiSchema) {
      const scope = this.responseScope(lastFailure, lastFailureIsNetwork, lastFailureIsTimeout)
      return (
        <>
          <UI2FireLazyContent uiSchema={lazyFailureUiSchema} scope={scope} frames={frames} />
          {retryActive && fireMode !== 'inlineLookup' && this.renderRetryButton()}
        </>
      )
    }

    if (!mustSurface && !failureMessageExpression) return null

    const title = lastFailureIsTimeout
      ? FIRE_DEFAULT_TIMEOUT_TITLE
      : lastFailureIsNetwork
        ? FIRE_DEFAULT_NO_INTERNET_TITLE
        : DEFAULT_FAILURE_TITLE
    const body = this.buildFailureBody(
      lastFailure, lastFailureIsNetwork, lastFailureIsTimeout, lastFailureClass,
    )
    return (
      <div className='ui2-fireField__failureBanner'>
        <UI2Banner mode='danger' title={title} markdownText={body}>
          {retryActive && fireMode !== 'inlineLookup' && this.renderRetryButton()}
        </UI2Banner>
      </div>
    )
  }

  private renderDebugPanel(): React.ReactNode {
    if (!this.props.debug) return null
    if (isProductionApi(apis.getHost())) return null
    const { value, eventType, fireMode, dryRun, valueMappings } = this.props
    const { lastFailure, isLoading, attemptsUsed } = this.state
    return (
      <UI2FireDebugPanel
        title='[eventField debug]'
        isLoading={isLoading}
        rows={[
          { label: 'eventType', value: eventType },
          { label: 'fireMode', value: String(fireMode) },
          { label: 'dryRun', value: String(dryRun !== false) },
          { label: 'executionId', value: this.getExecutionId() ?? '<none>' },
          { label: 'returnPaths', value: eventReturnPaths(valueMappings).join(', ') || '<none>' },
          {
            label: 'destinations',
            value: _.map(valueMappings, 'destination').filter(Boolean).join(', ') || '<none>',
          },
          {
            label: 'resolvedValues',
            value: _.isEmpty(value?.resolvedValues)
              ? '<none returned>'
              : safeStringify(value?.resolvedValues),
          },
          {
            label: 'mappedWrites',
            value: value
              ? (_.map(
                  resolveEventMappings(valueMappings, this.props.frames, value),
                  (m) => `${m.destination}=${safeStringify(m.value)}`,
                ).join(', ') || '<none resolved>')
              : '<not fired>',
          },
          { label: 'attemptsUsed', value: String(attemptsUsed) },
          { label: 'isLoading', value: String(isLoading) },
        ]}
        blocks={[
          { label: 'value', content: value ? safeStringify(value) : '<none>' },
          ...(lastFailure
            ? [{ label: 'lastFailure', content: safeStringify(lastFailure) }]
            : []),
        ]}
      />
    )
  }

  private renderTriggerButton(): React.ReactNode {
    const { buttonLabel, fireButtonVariant, isDisabled } = this.props
    return (
      <UI2FireTriggerButton
        label={buttonLabel}
        variant={resolveFireButtonVariant(fireButtonVariant, FIRE_DEFAULT_FIRE_BUTTON_VARIANT)}
        isDisabled={isDisabled}
        isLoading={this.state.isLoading}
        onPress={this.handleClick}
      />
    )
  }

  /**
   * The lookup input stays mounted across the whole cycle — it *is* the retry affordance, so it is
   * disabled rather than replaced once the attempt limit is reached.
   */
  private renderInlineLookup(): React.ReactNode {
    const { lookupPlaceholder, minQueryLength, isDisabled } = this.props
    return (
      <UI2FireInlineLookup
        placeholder={lookupPlaceholder}
        minQueryLength={minQueryLength}
        isDisabled={isDisabled}
        isLoading={this.state.isLoading}
        isSubmitDisabled={this.state.lastFailureClass === 'exhausted'}
        onSubmit={this.handleLookupSubmit}
      />
    )
  }

  /**
   * Read-only shows only what the schema declared — there is no retry here, so the "always surface
   * a retryable failure" exception doesn't apply and an undeclared outcome stays silent.
   */
  private renderReadOnly(): React.ReactNode {
    const { value } = this.props
    if (!value) return null
    const content = value.success ? this.renderSuccess(value) : this.renderReadOnlyFailure(value)
    if (!content) return null
    return <div className='ui2-fireField__inner'>{content}</div>
  }

  private renderReadOnlyFailure(value: EventFieldResponse): React.ReactNode {
    const { lazyFailureUiSchema, failureMessageExpression, frames } = this.props
    if (lazyFailureUiSchema) {
      return (
        <UI2FireLazyContent
          uiSchema={lazyFailureUiSchema}
          scope={this.responseScope(value)}
          frames={frames}
        />
      )
    }
    if (!failureMessageExpression) return null
    const body = this.buildFailureBody(value, false, false, 'notEligible')
    return (
      <div className='ui2-fireField__failureBanner'>
        <UI2Banner mode='danger' title={DEFAULT_FAILURE_TITLE} markdownText={body} />
      </div>
    )
  }

  private renderBody(): React.ReactNode {
    const { fireMode } = this.props
    const { hasSucceeded, lastFailure } = this.state
    const loading = this.renderLoading()
    const success = hasSucceeded ? this.renderSuccess() : null
    const failure = this.renderFailure()
    const debug = this.renderDebugPanel()

    if (fireMode === 'inlineLookup') {
      return (
        <div className='ui2-fireField__inner'>
          {this.renderInlineLookup()}
          {loading}
          {success}
          {failure}
          {debug}
        </div>
      )
    }

    if (fireMode === 'onClick') {
      const showButton = !hasSucceeded && !lastFailure && !loading
      return (
        <div className='ui2-fireField__inner'>
          {showButton && this.renderTriggerButton()}
          {loading}
          {success}
          {failure}
          {debug}
        </div>
      )
    }

    if (!loading && !success && !failure && !debug) return null
    return (
      <div className='ui2-fireField__inner'>
        {loading}
        {success}
        {failure}
        {debug}
      </div>
    )
  }

  public render() {
    const { className, fireMode, isReadOnly, errors } = this.props
    const { lastFailureClass } = this.state

    if (isReadOnly) {
      const banners = this.renderReadOnly()
      if (isFireInteractiveMode(fireMode)) {
        return (
          <UI2FieldShell
            {...(this.props as unknown as IUI2FieldShellProps)}
            isValueEmpty={isFireResponseIncomplete}
            className={classNames('ui2-eventField', className)}
            suppressDefaultEmptyState
            renderReadOnly={(_v, { editNode }) => <><div className='ui2-fieldShell-readOnly-content'>{banners}</div>{editNode}</>}
          >
            {null}
          </UI2FieldShell>
        )
      }
      if (!banners) return null
      return <div className={classNames('ui2-eventField', className)}>{banners}</div>
    }

    const body = this.renderBody()

    // Auto-fire modes render bare, with two exceptions that need the shell: a retryable onMount
    // failure (so label + help text sit alongside the retry UI) and an error to report — the shell
    // is the only thing that renders one, so a required field that never succeeded would otherwise
    // fail submit with nothing on screen to explain why.
    if (!isFireInteractiveMode(fireMode)) {
      const hasError = !_.isEmpty(errors)
      if (body === null && !hasError) return null
      if (hasError || (fireMode === 'onMount' && lastFailureClass === 'retry')) {
        return (
          <UI2FieldShell
            {...(this.props as unknown as IUI2FieldShellProps)}
            isValueEmpty={isFireResponseIncomplete}
            className={classNames('ui2-eventField', className)}
          >
            {body}
          </UI2FieldShell>
        )
      }
      return (
        <div className={classNames('ui2-eventField', className)}>
          {body}
        </div>
      )
    }

    return (
      <UI2FieldShell
        {...(this.props as unknown as IUI2FieldShellProps)}
        isValueEmpty={isFireResponseIncomplete}
        className={classNames('ui2-eventField', className)}
      >
        {body}
      </UI2FieldShell>
    )
  }
}
